import React, { useState, useEffect, useRef, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import './App.css';

// In development, call backend directly to avoid proxy buffering SSE
const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '';

// Fetch options with credentials for cookie-based auth
const fetchWithCredentials = (url, options = {}) => {
  return fetch(url, {
    ...options,
    credentials: 'include',
  });
};

function App() {
  const [activeTab, setActiveTab] = useState('transform'); // 'transform', 'rewrite', 'styles', 'admin'
  const [inputText, setInputText] = useState('');
  const [transformOutput, setTransformOutput] = useState('');
  const [rewriteOutput, setRewriteOutput] = useState('');
  const [intensity, setIntensity] = useState('medium');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingType, setLoadingType] = useState(null); // 'transform' or 'rewrite'
  const [error, setError] = useState('');
  const [copiedTransform, setCopiedTransform] = useState(false);
  const [copiedRewrite, setCopiedRewrite] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [songTitle, setSongTitle] = useState('');
  const [isStreamingTransform, setIsStreamingTransform] = useState(false);
  const [isStreamingRewrite, setIsStreamingRewrite] = useState(false);

  // Artist search state
  const [artists, setArtists] = useState([]);
  const [artistSearch, setArtistSearch] = useState('');
  const [selectedArtistStyle, setSelectedArtistStyle] = useState('');
  const [showArtistDropdown, setShowArtistDropdown] = useState(false);
  const [isLoadingStyle, setIsLoadingStyle] = useState(false);
  const [styleCopied, setStyleCopied] = useState(false);
  const artistSearchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Auth state
  const [user, setUser] = useState(null);
  const [credits, setCredits] = useState(0);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  
  // Modal states
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showOutOfCreditsModal, setShowOutOfCreditsModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Auth form state
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  
  // Credit packages
  const [creditPackages, setCreditPackages] = useState([]);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  
  // Admin state
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', credits: 0, is_admin: false });
  const [adminError, setAdminError] = useState('');
  const [adminSaving, setAdminSaving] = useState(false);
  
  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Fetch current user on mount
  const fetchUser = useCallback(async () => {
    try {
      const response = await fetchWithCredentials(`${API_BASE}/api/auth/me`);
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setCredits(data.credits || 0);
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
    } finally {
      setIsAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Show welcome modal for first-time visitors
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('lyricSwapperWelcomeSeen');
    if (!hasSeenWelcome) {
      setShowWelcomeModal(true);
    }
  }, []);

  const dismissWelcomeModal = () => {
    localStorage.setItem('lyricSwapperWelcomeSeen', 'true');
    setShowWelcomeModal(false);
  };

  // Restore saved state from localStorage (for returning from Stripe checkout)
  useEffect(() => {
    const savedState = localStorage.getItem('lyricSwapperState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        if (state.inputText) setInputText(state.inputText);
        if (state.transformOutput) setTransformOutput(state.transformOutput);
        if (state.rewriteOutput) setRewriteOutput(state.rewriteOutput);
        if (state.songTitle) setSongTitle(state.songTitle);
        if (state.activeTab) setActiveTab(state.activeTab);
        if (state.intensity) setIntensity(state.intensity);
        // Clear saved state after restoring
        localStorage.removeItem('lyricSwapperState');
      } catch (err) {
        console.error('Failed to restore saved state:', err);
        localStorage.removeItem('lyricSwapperState');
      }
    }
  }, []);

  // Check for payment success/cancelled in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const creditsAdded = params.get('credits');
    
    if (payment === 'success' && creditsAdded) {
      setModalMessage(`Payment successful! ${creditsAdded} credits have been added to your account.`);
      setShowModal(true);
      // Refresh user data
      fetchUser();
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    } else if (payment === 'cancelled') {
      setModalMessage('Payment was cancelled.');
      setShowModal(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchUser]);

  // Fetch credit packages
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/credits/packages`);
        if (response.ok) {
          const data = await response.json();
          setCreditPackages(data.packages || []);
        }
      } catch (err) {
        console.error('Failed to fetch packages:', err);
      }
    };
    fetchPackages();
  }, []);

  // Fetch artists on mount
  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const response = await fetch('/api/artists');
        if (response.ok) {
          const data = await response.json();
          setArtists(data.artists || []);
        }
      } catch (err) {
        console.error('Failed to fetch artists:', err);
      }
    };
    fetchArtists();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        artistSearchRef.current && 
        !artistSearchRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowArtistDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter artists based on search
  const filteredArtists = artists.filter(artist =>
    artist.toLowerCase().includes(artistSearch.toLowerCase())
  );

  // Handle artist selection
  const handleSelectArtist = async (artistName) => {
    setArtistSearch(artistName);
    setShowArtistDropdown(false);
    setIsLoadingStyle(true);
    setSelectedArtistStyle('');

    try {
      const response = await fetch(`/api/artists/${encodeURIComponent(artistName)}/style`);
      if (response.ok) {
        const data = await response.json();
        setSelectedArtistStyle(data.style);
      } else {
        setSelectedArtistStyle('Style not found');
      }
    } catch (err) {
      setSelectedArtistStyle('Failed to load style');
    } finally {
      setIsLoadingStyle(false);
    }
  };

  // Copy style to clipboard
  const handleCopyStyle = async () => {
    if (!selectedArtistStyle) return;
    try {
      await navigator.clipboard.writeText(selectedArtistStyle);
      setStyleCopied(true);
      setTimeout(() => setStyleCopied(false), 2000);
    } catch (err) {
      setModalMessage('Failed to copy style to clipboard');
      setShowModal(true);
    }
  };

  // ============== Auth Handlers ==============
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    
    try {
      const response = await fetchWithCredentials(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authEmail, password: authPassword }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }
      
      setUser(data);
      setCredits(data.credits || 0);
      setShowLoginModal(false);
      setAuthEmail('');
      setAuthPassword('');
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };
  
  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    
    try {
      const response = await fetchWithCredentials(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: authName, 
          email: authEmail, 
          password: authPassword 
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }
      
      setUser(data);
      setCredits(data.credits || 0);
      setShowRegisterModal(false);
      setAuthName('');
      setAuthEmail('');
      setAuthPassword('');
      
      // If we were trying to purchase, open purchase modal
      if (showPurchaseModal) {
        // Keep purchase modal open
      }
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await fetchWithCredentials(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
      });
      setUser(null);
      setCredits(0);
      setShowUserMenu(false);
      // Refresh to get new IP-based user
      fetchUser();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };
  
  const handlePurchase = async (packageId) => {
    // Must be registered to purchase
    if (!user?.isRegistered) {
      setShowPurchaseModal(false);
      setShowRegisterModal(true);
      return;
    }
    
    setPurchaseLoading(true);
    
    try {
      const response = await fetchWithCredentials(`${API_BASE}/api/credits/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package: packageId }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (data.needsRegistration) {
          setShowPurchaseModal(false);
          setShowRegisterModal(true);
          return;
        }
        throw new Error(data.error || 'Purchase failed');
      }
      
      // Save current state to localStorage before redirecting
      if (data.checkoutUrl) {
        const stateToSave = {
          inputText,
          transformOutput,
          rewriteOutput,
          songTitle,
          activeTab,
          intensity,
          savedAt: Date.now()
        };
        localStorage.setItem('lyricSwapperState', JSON.stringify(stateToSave));
        
        // Redirect to Stripe checkout
        window.location.href = data.checkoutUrl;
      }
    } catch (err) {
      setModalMessage(err.message);
      setShowModal(true);
    } finally {
      setPurchaseLoading(false);
    }
  };
  
  const openPurchaseModal = () => {
    setShowOutOfCreditsModal(false);
    if (!user?.isRegistered) {
      setShowRegisterModal(true);
    } else {
      setShowPurchaseModal(true);
    }
  };

  // ============== Admin Handlers ==============
  
  const fetchAdminUsers = useCallback(async () => {
    if (!user?.isAdmin) return;
    
    setAdminLoading(true);
    try {
      const response = await fetchWithCredentials(`${API_BASE}/api/admin/users`);
      if (response.ok) {
        const data = await response.json();
        setAdminUsers(data.users || []);
      }
    } catch (err) {
      console.error('Failed to fetch admin users:', err);
    } finally {
      setAdminLoading(false);
    }
  }, [user?.isAdmin]);
  
  useEffect(() => {
    if (activeTab === 'admin' && user?.isAdmin) {
      fetchAdminUsers();
    }
  }, [activeTab, user?.isAdmin, fetchAdminUsers]);
  
  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditForm({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      credits: userToEdit.credits || 0,
      is_admin: userToEdit.is_admin || false
    });
    setAdminError('');
    setShowEditUserModal(true);
  };
  
  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    
    setAdminSaving(true);
    setAdminError('');
    
    try {
      const response = await fetchWithCredentials(`${API_BASE}/api/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update user');
      }
      
      // Update local state
      setAdminUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...data } : u));
      setShowEditUserModal(false);
      setEditingUser(null);
    } catch (err) {
      setAdminError(err.message);
    } finally {
      setAdminSaving(false);
    }
  };
  
  const handleDeleteUser = (userToDelete) => {
    setDeletingUser(userToDelete);
    setShowDeleteConfirmModal(true);
  };
  
  const confirmDeleteUser = async () => {
    if (!deletingUser) return;
    
    setAdminSaving(true);
    
    try {
      const response = await fetchWithCredentials(`${API_BASE}/api/admin/users/${deletingUser.id}`, {
        method: 'DELETE',
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete user');
      }
      
      // Remove from local state
      setAdminUsers(prev => prev.filter(u => u.id !== deletingUser.id));
      setShowDeleteConfirmModal(false);
      setDeletingUser(null);
    } catch (err) {
      setModalMessage(err.message);
      setShowModal(true);
    } finally {
      setAdminSaving(false);
    }
  };

  const handleObfuscate = async () => {
    if (!inputText.trim()) {
      setModalMessage('Please enter some text to transform.');
      setShowModal(true);
      return;
    }

    setIsLoading(true);
    setLoadingType('transform');
    setError('');
    setTransformOutput('');

    try {
      // Start the job
      const response = await fetchWithCredentials(`${API_BASE}/api/obfuscate/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: inputText,
          intensity: intensity,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        // Handle insufficient credits
        if (response.status === 402 || data.needsCredits) {
          setCredits(0);
          setShowOutOfCreditsModal(true);
          return;
        }
        throw new Error(data.error || 'Something went wrong');
      }

      const { jobId } = await response.json();
      setIsStreamingTransform(true);

      // Poll for results
      let lastTextLength = 0;
      while (true) {
        await new Promise(resolve => setTimeout(resolve, 200)); // Poll every 200ms
        
        const statusResponse = await fetchWithCredentials(`${API_BASE}/api/job/${jobId}`);
        if (!statusResponse.ok) {
          throw new Error('Failed to get job status');
        }
        
        const status = await statusResponse.json();
        
        if (status.error) {
          throw new Error(status.error);
        }
        
        // Update text if there's new content
        if (status.text.length > lastTextLength) {
          setTransformOutput(status.text);
          lastTextLength = status.text.length;
        }
        
        // Update credits if returned
        if (status.credits !== undefined) {
          setCredits(status.credits);
        }
        
        if (status.done) {
          setIsStreamingTransform(false);
          break;
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
      setLoadingType(null);
      setIsStreamingTransform(false);
    }
  };

  const handleRewrite = async () => {
    if (!inputText.trim()) {
      setModalMessage('Please enter some lyrics to rewrite.');
      setShowModal(true);
      return;
    }

    setIsLoading(true);
    setLoadingType('rewrite');
    setError('');
    setRewriteOutput('');

    try {
      // Start the job
      const response = await fetchWithCredentials(`${API_BASE}/api/rewrite/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: inputText,
          songTitle: songTitle,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        // Handle insufficient credits
        if (response.status === 402 || data.needsCredits) {
          setCredits(0);
          setShowOutOfCreditsModal(true);
          return;
        }
        throw new Error(data.error || 'Something went wrong');
      }

      const { jobId } = await response.json();
      setIsStreamingRewrite(true);

      // Poll for results
      let lastTextLength = 0;
      while (true) {
        await new Promise(resolve => setTimeout(resolve, 200)); // Poll every 200ms
        
        const statusResponse = await fetchWithCredentials(`${API_BASE}/api/job/${jobId}`);
        if (!statusResponse.ok) {
          throw new Error('Failed to get job status');
        }
        
        const status = await statusResponse.json();
        
        if (status.error) {
          throw new Error(status.error);
        }
        
        // Update text if there's new content
        if (status.text.length > lastTextLength) {
          setRewriteOutput(status.text);
          lastTextLength = status.text.length;
        }
        
        // Update credits if returned
        if (status.credits !== undefined) {
          setCredits(status.credits);
        }
        
        if (status.done) {
          setIsStreamingRewrite(false);
          break;
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
      setLoadingType(null);
      setIsStreamingRewrite(false);
    }
  };

  const handleCopyTransform = async () => {
    if (!transformOutput) return;
    
    try {
      await navigator.clipboard.writeText(transformOutput);
      setCopiedTransform(true);
      setTimeout(() => setCopiedTransform(false), 2000);
    } catch (err) {
      setModalMessage('Failed to copy to clipboard');
      setShowModal(true);
    }
  };

  const handleCopyRewrite = async () => {
    if (!rewriteOutput) return;
    
    try {
      await navigator.clipboard.writeText(rewriteOutput);
      setCopiedRewrite(true);
      setTimeout(() => setCopiedRewrite(false), 2000);
    } catch (err) {
      setModalMessage('Failed to copy to clipboard');
      setShowModal(true);
    }
  };

  const handleClear = () => {
    setInputText('');
    setTransformOutput('');
    setRewriteOutput('');
    setError('');
  };

  const handleExportPDF = () => {
    if (!rewriteOutput && !transformOutput) {
      setModalMessage('No output to export. Please generate rewritten or transformed lyrics first.');
      setShowModal(true);
      return;
    }

    const title = songTitle.trim() || 'Untitled Song';
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    
    // Helper function to add text with word wrap
    const addWrappedText = (text, startY) => {
      const lines = doc.splitTextToSize(text, maxWidth);
      let y = startY;
      const lineHeight = 7;
      const pageHeight = doc.internal.pageSize.getHeight();
      
      for (let i = 0; i < lines.length; i++) {
        if (y + lineHeight > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(lines[i], margin, y);
        y += lineHeight;
      }
      return y;
    };

    // Page 1: Rewritten Lyrics (if exists)
    if (rewriteOutput) {
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(title, margin, margin + 10);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text('Syllable-Matched Rewrite', margin, margin + 20);
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      addWrappedText(rewriteOutput, margin + 35);
    }

    // Page 2: Transformed Lyrics (if exists)
    if (transformOutput) {
      if (rewriteOutput) {
        doc.addPage();
      }
      
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(title, margin, margin + 10);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text('Phonetic Transform', margin, margin + 20);
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      addWrappedText(transformOutput, margin + 35);
    }

    // Generate filename
    const safeTitle = title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    doc.save(`${safeTitle}_lyrics.pdf`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleObfuscate();
    }
  };

  return (
    <div className="app">
      {/* Generic Message Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)} role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <p id="modal-title">{modalMessage}</p>
            <button 
              className="modal-button" 
              onClick={() => setShowModal(false)}
              aria-label="Close modal"
            >
              OK
            </button>
          </div>
        </div>
      )}
      
      {/* Login Modal */}
      {showLoginModal && (
        <div className="modal-overlay" onClick={() => setShowLoginModal(false)} role="dialog" aria-modal="true" aria-labelledby="login-title">
          <div className="modal modal-auth" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowLoginModal(false)} aria-label="Close">
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </button>
            <h2 id="login-title">Welcome Back</h2>
            <p className="modal-subtitle">Sign in to your account</p>
            
            <form onSubmit={handleLogin} className="auth-form">
              {authError && <div className="auth-error">{authError}</div>}
              
              <div className="form-group">
                <label htmlFor="login-email">Email</label>
                <input
                  id="login-email"
                  type="email"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  autoComplete="email"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="login-password">Password</label>
                <input
                  id="login-password"
                  type="password"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  placeholder="Your password"
                  required
                  autoComplete="current-password"
                />
              </div>
              
              <button type="submit" className="btn btn-primary btn-full" disabled={authLoading}>
                {authLoading ? <><div className="spinner"></div>Signing in...</> : 'Sign In'}
              </button>
            </form>
            
            <p className="modal-footer-text">
              Don't have an account?{' '}
              <button className="link-btn" onClick={() => { setShowLoginModal(false); setShowRegisterModal(true); setAuthError(''); }}>
                Sign up
              </button>
            </p>
          </div>
        </div>
      )}
      
      {/* Register Modal */}
      {showRegisterModal && (
        <div className="modal-overlay" onClick={() => setShowRegisterModal(false)} role="dialog" aria-modal="true" aria-labelledby="register-title">
          <div className="modal modal-auth" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowRegisterModal(false)} aria-label="Close">
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </button>
            <h2 id="register-title">Create Account</h2>
            <p className="modal-subtitle">Sign up to purchase credits and save your account</p>
            
            <form onSubmit={handleRegister} className="auth-form">
              {authError && <div className="auth-error">{authError}</div>}
              
              <div className="form-group">
                <label htmlFor="register-name">Name</label>
                <input
                  id="register-name"
                  type="text"
                  value={authName}
                  onChange={(e) => setAuthName(e.target.value)}
                  placeholder="Your name"
                  required
                  autoComplete="name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="register-email">Email</label>
                <input
                  id="register-email"
                  type="email"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  autoComplete="email"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="register-password">Password</label>
                <input
                  id="register-password"
                  type="password"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
              
              <button type="submit" className="btn btn-primary btn-full" disabled={authLoading}>
                {authLoading ? <><div className="spinner"></div>Creating account...</> : 'Create Account'}
              </button>
            </form>
            
            <p className="modal-footer-text">
              Already have an account?{' '}
              <button className="link-btn" onClick={() => { setShowRegisterModal(false); setShowLoginModal(true); setAuthError(''); }}>
                Sign in
              </button>
            </p>
          </div>
        </div>
      )}
      
      {/* Purchase Credits Modal */}
      {showPurchaseModal && (
        <div className="modal-overlay" onClick={() => setShowPurchaseModal(false)} role="dialog" aria-modal="true" aria-labelledby="purchase-title">
          <div className="modal modal-purchase" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPurchaseModal(false)} aria-label="Close">
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </button>
            <h2 id="purchase-title">Buy Credits</h2>
            <p className="modal-subtitle">Each credit = 1 transform or rewrite generation</p>
            
            <div className="credit-packages">
              {creditPackages.map((pkg, index) => (
                <div 
                  key={pkg.id} 
                  className={`credit-package ${index === 1 ? 'popular' : ''}`}
                >
                  {index === 1 && <span className="popular-badge">Best Value</span>}
                  <div className="package-credits">{pkg.credits}</div>
                  <div className="package-label">credits</div>
                  <div className="package-price">{pkg.price}</div>
                  <div className="package-per-credit">
                    ${(pkg.amount / pkg.credits / 100).toFixed(2)}/credit
                  </div>
                  <button 
                    className="btn btn-package"
                    onClick={() => handlePurchase(pkg.id)}
                    disabled={purchaseLoading}
                    aria-label={`Buy ${pkg.credits} credits for ${pkg.price}`}
                  >
                    {purchaseLoading ? <div className="spinner"></div> : 'Buy Now'}
                  </button>
                </div>
              ))}
            </div>
            
            <p className="purchase-note">
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
              Secure payment powered by Stripe
            </p>
          </div>
        </div>
      )}
      
      {/* Out of Credits Modal */}
      {showOutOfCreditsModal && (
        <div className="modal-overlay" onClick={() => setShowOutOfCreditsModal(false)} role="dialog" aria-modal="true" aria-labelledby="no-credits-title">
          <div className="modal modal-no-credits" onClick={e => e.stopPropagation()}>
            <div className="no-credits-icon">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 id="no-credits-title">Out of Credits</h2>
            <p>You've used all your credits. Purchase more to continue generating lyrics.</p>
            
            <div className="no-credits-actions">
              <button 
                className="btn btn-primary"
                onClick={openPurchaseModal}
              >
                Buy Credits
              </button>
              <button 
                className="btn btn-ghost"
                onClick={() => setShowOutOfCreditsModal(false)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit User Modal (Admin) */}
      {showEditUserModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowEditUserModal(false)} role="dialog" aria-modal="true" aria-labelledby="edit-user-title">
          <div className="modal modal-admin-edit" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowEditUserModal(false)} aria-label="Close">
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </button>
            <h2 id="edit-user-title">Edit User #{editingUser.id}</h2>
            
            <form onSubmit={handleSaveUser} className="auth-form">
              {adminError && <div className="auth-error">{adminError}</div>}
              
              <div className="form-group">
                <label htmlFor="edit-name">Name</label>
                <input
                  id="edit-name"
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="User name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-email">Email</label>
                <input
                  id="edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="user@email.com"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-credits">Credits</label>
                <input
                  id="edit-credits"
                  type="number"
                  min="0"
                  value={editForm.credits}
                  onChange={(e) => setEditForm(prev => ({ ...prev, credits: parseInt(e.target.value) || 0 }))}
                />
              </div>
              
              {editingUser.id !== user?.id && (
                <div className="form-group form-group-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={editForm.is_admin}
                      onChange={(e) => setEditForm(prev => ({ ...prev, is_admin: e.target.checked }))}
                    />
                    <span>Administrator</span>
                  </label>
                </div>
              )}
              
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowEditUserModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={adminSaving}>
                  {adminSaving ? <><div className="spinner"></div>Saving...</> : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal (Admin) */}
      {showDeleteConfirmModal && deletingUser && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirmModal(false)} role="dialog" aria-modal="true" aria-labelledby="delete-user-title">
          <div className="modal modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon confirm-icon-danger">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 id="delete-user-title">Delete User?</h2>
            <p>Are you sure you want to delete <strong>{deletingUser.name || deletingUser.email || `User #${deletingUser.id}`}</strong>? This action cannot be undone.</p>
            
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowDeleteConfirmModal(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteUser} disabled={adminSaving}>
                {adminSaving ? <><div className="spinner"></div>Deleting...</> : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Welcome Modal for First-Time Users */}
      {showWelcomeModal && (
        <div className="modal-overlay" onClick={dismissWelcomeModal} role="dialog" aria-modal="true" aria-labelledby="welcome-title">
          <div className="modal modal-welcome" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={dismissWelcomeModal} aria-label="Close welcome message">
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </button>
            
            <div className="welcome-icon" aria-hidden="true">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4L4 12V28L20 36L36 28V12L20 4Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                <path d="M12 18C12 18 14 22 20 22C26 22 28 18 28 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="14" cy="14" r="2" fill="currentColor"/>
                <circle cx="26" cy="14" r="2" fill="currentColor"/>
              </svg>
            </div>
            
            <h2 id="welcome-title">Welcome to Lyric Swapper!</h2>
            
            <p className="welcome-subtitle">
              Your secret weapon for AI music generation
            </p>
            
            <div className="welcome-content">
              <p>
                AI music tools like <strong>Suno</strong> and <strong>Udio</strong> block copyrighted lyrics. 
                Lyric Swapper helps you work around this by modifying lyrics while keeping them <em>singable</em>.
              </p>
              
              <div className="welcome-features">
                <div className="welcome-feature">
                  <div className="feature-icon feature-icon-transform" aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="feature-text">
                    <strong>Transform</strong>
                    <span>For pasting into Suno/Udio — homophones that sound identical when sung</span>
                    <code>"I love you" → "Eye love ewe"</code>
                  </div>
                </div>
                
                <div className="welcome-feature">
                  <div className="feature-icon feature-icon-rewrite" aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                      <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="feature-text">
                    <strong>Rewrite</strong>
                    <span>For musicians to sing — new words with matching syllable counts</span>
                    <code>Same melody, no copyright issues!</code>
                  </div>
                </div>
              </div>
              
              <p className="welcome-note">
                Transform lyrics go into Suno's lyrics field. Rewrite lyrics are for you to sing yourself!
              </p>
            </div>
            
            <button 
              className="btn btn-primary btn-full welcome-btn" 
              onClick={dismissWelcomeModal}
              aria-label="Get started with Lyric Swapper"
            >
              Get Started
            </button>
          </div>
        </div>
      )}

      <header className="header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-icon" aria-hidden="true">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4L4 12V28L20 36L36 28V12L20 4Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                <path d="M12 18C12 18 14 22 20 22C26 22 28 18 28 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="14" cy="14" r="2" fill="currentColor"/>
                <circle cx="26" cy="14" r="2" fill="currentColor"/>
              </svg>
            </div>
            <h1 className="logo-text">Lyric Swapper</h1>
          </div>
          
          <div className="header-actions">
            {(rewriteOutput || transformOutput) && (
              <button 
                className="btn btn-export" 
                onClick={handleExportPDF}
                aria-label="Export lyrics to PDF"
              >
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                </svg>
                <span>Export PDF</span>
              </button>
            )}
            
            {/* Credits Badge */}
            {!isAuthLoading && (
              <button 
                className="credits-badge" 
                onClick={() => setShowPurchaseModal(true)}
                aria-label={`${credits} credits remaining. Click to purchase more.`}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a4.265 4.265 0 01-.264-.521H10a1 1 0 100-2H8.017a7.36 7.36 0 010-1H10a1 1 0 100-2H8.472c.08-.185.167-.36.264-.521z" />
                </svg>
                <span>{credits}</span>
              </button>
            )}
            
            {/* Auth Section */}
            {!isAuthLoading && (
              <div className="auth-section">
                {user?.isRegistered ? (
                  <div className="user-menu-container">
                    <button 
                      className="user-menu-btn"
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      aria-label="User menu"
                      aria-expanded={showUserMenu}
                    >
                      <span className="user-avatar">
                        {user.name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                      <span className="user-name">{user.name}</span>
                      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {showUserMenu && (
                      <div className="user-dropdown-menu">
                        <div className="user-dropdown-info">
                          <span className="user-dropdown-name">{user.name}</span>
                          <span className="user-dropdown-email">{user.email}</span>
                        </div>
                        <button onClick={() => { setShowUserMenu(false); setShowPurchaseModal(true); }}>
                          <svg viewBox="0 0 20 20" fill="currentColor"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a4.265 4.265 0 01-.264-.521H10a1 1 0 100-2H8.017a7.36 7.36 0 010-1H10a1 1 0 100-2H8.472c.08-.185.167-.36.264-.521z" /></svg>
                          Buy Credits
                        </button>
                        {user.isAdmin && (
                          <button onClick={() => { setShowUserMenu(false); setActiveTab('admin'); }}>
                            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" /></svg>
                            Admin Panel
                          </button>
                        )}
                        <button onClick={handleLogout}>
                          <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" /></svg>
                          Sign Out
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="auth-buttons">
                    <button 
                      className="btn btn-ghost"
                      onClick={() => setShowLoginModal(true)}
                      aria-label="Sign in"
                    >
                      Sign In
                    </button>
                    <button 
                      className="btn btn-primary-sm"
                      onClick={() => setShowRegisterModal(true)}
                      aria-label="Create account"
                    >
                      Sign Up
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="container">
          {/* Tab Navigation */}
          <nav className="tab-nav" role="tablist" aria-label="Main navigation">
            <button
              role="tab"
              aria-selected={activeTab === 'transform'}
              aria-controls="transform-panel"
              className={`tab-btn ${activeTab === 'transform' ? 'active' : ''}`}
              onClick={() => setActiveTab('transform')}
            >
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              <span>Transform</span>
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'rewrite'}
              aria-controls="rewrite-panel"
              className={`tab-btn ${activeTab === 'rewrite' ? 'active' : ''}`}
              onClick={() => setActiveTab('rewrite')}
            >
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" />
              </svg>
              <span>Rewrite</span>
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'styles'}
              aria-controls="styles-panel"
              className={`tab-btn ${activeTab === 'styles' ? 'active' : ''}`}
              onClick={() => setActiveTab('styles')}
            >
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
              </svg>
              <span>Styles</span>
            </button>
          </nav>

          {/* Transform Tab */}
          {activeTab === 'transform' && (
            <div id="transform-panel" role="tabpanel" aria-labelledby="transform-tab" className="tab-panel">
              {/* Intro - What this does */}
              <div className="tab-intro tab-intro-transform">
                <h2>Phonetic Transform</h2>
                <p><strong>For pasting into Suno/Udio's lyrics field.</strong> Swaps words with homophones — words that sound identical but are spelled differently. When the AI sings these lyrics, the song sounds exactly the same, but the text won't be flagged for copyright.</p>
                <div className="tab-example">
                  <span className="example-before">"I love you"</span>
                  <span className="example-arrow">→</span>
                  <span className="example-after">"Eye love ewe"</span>
                </div>
                <p className="tab-note">Copy the transformed output and paste it directly into Suno's "Lyrics" field. The AI will sing it identically to the original!</p>
              </div>

              {/* Text Areas Grid */}
              <div className="text-grid">
                <div className="text-section">
                  <div className="section-header">
                    <label htmlFor="input-text-transform" className="section-title">Original Lyrics</label>
                    <span className="char-count">{inputText.length} / 5000</span>
                  </div>
                  <textarea
                    id="input-text-transform"
                    className="text-area"
                    placeholder="Paste your lyrics here..."
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    maxLength={5000}
                  />
                </div>
                <div className="text-section">
                  <div className="section-header">
                    <label htmlFor="output-text-transform" className="section-title">Transformed Output</label>
                    {transformOutput && (
                      <button className={`copy-btn ${copiedTransform ? 'copied' : ''}`} onClick={handleCopyTransform} aria-label="Copy transformed text">
                        {copiedTransform ? 'Copied!' : 'Copy'}
                      </button>
                    )}
                  </div>
                  <textarea
                    id="output-text-transform"
                    className={`text-area output ${isStreamingTransform ? 'streaming' : ''} ${loadingType === 'transform' && !transformOutput ? 'thinking' : ''}`}
                    placeholder="Transformed text will appear here..."
                    value={loadingType === 'transform' && !transformOutput ? 'AI is thinking...' : transformOutput}
                    readOnly
                  />
                </div>
              </div>

              {/* Action Bar */}
              <div className="action-bar action-bar-transform">
                <div className="intensity-section-inline">
                  <label className="intensity-label-inline">Swap Intensity</label>
                  <div className="intensity-buttons">
                    {['low', 'medium', 'high'].map((level) => (
                      <button
                        key={level}
                        className={`intensity-btn ${intensity === level ? 'active' : ''}`}
                        onClick={() => setIntensity(level)}
                      >
                        <span className="intensity-name">{level}</span>
                        <span className="intensity-desc">
                          {level === 'low' && '~10%'}
                          {level === 'medium' && '~15%'}
                          {level === 'high' && '~25%'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="action-buttons">
                  <button
                    className="btn btn-primary"
                    onClick={handleObfuscate}
                    disabled={isLoading || !inputText.trim()}
                    aria-label="Transform lyrics"
                  >
                    {loadingType === 'transform' ? (
                      <><div className="spinner"></div>Transforming...</>
                    ) : (
                      <><svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" /></svg>Transform</>
                    )}
                  </button>
                  <button className="btn btn-secondary" onClick={handleClear} disabled={isLoading || (!inputText && !transformOutput && !rewriteOutput)} aria-label="Clear all text">
                    Clear
                  </button>
                </div>
              </div>

              {error && (
                <div className="error-message" role="alert">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Rewrite Tab */}
          {activeTab === 'rewrite' && (
            <div id="rewrite-panel" role="tabpanel" aria-labelledby="rewrite-tab" className="tab-panel">
              {/* Intro - What this does */}
              <div className="tab-intro tab-intro-rewrite">
                <h2>Syllable-Matching Rewrite</h2>
                <p><strong>For musicians to sing themselves.</strong> Completely rewrites lyrics with different words while keeping the <strong>exact same syllable count</strong> per line. Perfect for cover performances or recordings that won't trigger copyright claims.</p>
                <div className="tab-example">
                  <span className="example-before">"Amazing Grace, how sweet the sound" <small>(8 syl)</small></span>
                  <span className="example-arrow">→</span>
                  <span className="example-after">"Amazing Grace, I found the clown" <small>(8 syl)</small></span>
                </div>
                <p className="tab-note">The result may be nonsensical — that's intentional! Sing these new words to the original melody without copyright concerns.</p>
              </div>

              {/* Text Areas Grid */}
              <div className="text-grid">
                <div className="text-section">
                  <div className="section-header">
                    <label htmlFor="input-text-rewrite" className="section-title">Original Lyrics</label>
                    <span className="char-count">{inputText.length} / 5000</span>
                  </div>
                  <textarea
                    id="input-text-rewrite"
                    className="text-area"
                    placeholder="Paste your lyrics here..."
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    maxLength={5000}
                  />
                </div>
                <div className="text-section">
                  <div className="section-header">
                    <label htmlFor="output-text-rewrite" className="section-title">Rewritten Output</label>
                    {rewriteOutput && (
                      <button className={`copy-btn ${copiedRewrite ? 'copied' : ''}`} onClick={handleCopyRewrite} aria-label="Copy rewritten text">
                        {copiedRewrite ? 'Copied!' : 'Copy'}
                      </button>
                    )}
                  </div>
                  <textarea
                    id="output-text-rewrite"
                    className={`text-area output ${isStreamingRewrite ? 'streaming' : ''} ${loadingType === 'rewrite' && !rewriteOutput ? 'thinking' : ''}`}
                    placeholder="Rewritten lyrics will appear here..."
                    value={loadingType === 'rewrite' && !rewriteOutput ? 'AI is thinking...' : rewriteOutput}
                    readOnly
                  />
                </div>
              </div>

              {/* Action Bar */}
              <div className="action-bar action-bar-rewrite">
                <div className="song-title-inline">
                  <label className="song-title-label-inline" htmlFor="song-title">
                    Song Title (will be preserved in output)
                  </label>
                  <input
                    id="song-title"
                    type="text"
                    className="song-title-input"
                    placeholder="e.g., Amazing Grace, Bohemian Rhapsody..."
                    value={songTitle}
                    onChange={(e) => setSongTitle(e.target.value)}
                  />
                </div>
                <div className="action-buttons">
                  <button
                    className="btn btn-rewrite"
                    onClick={handleRewrite}
                    disabled={isLoading || !inputText.trim()}
                    aria-label="Rewrite lyrics"
                  >
                    {loadingType === 'rewrite' ? (
                      <><div className="spinner"></div>Rewriting...</>
                    ) : (
                      <><svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>Rewrite</>
                    )}
                  </button>
                  <button className="btn btn-secondary" onClick={handleClear} disabled={isLoading || (!inputText && !transformOutput && !rewriteOutput)} aria-label="Clear all text">
                    Clear
                  </button>
                </div>
              </div>

              {error && (
                <div className="error-message" role="alert">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Styles Tab */}
          {activeTab === 'styles' && (
            <div id="styles-panel" role="tabpanel" aria-labelledby="styles-tab" className="tab-panel">
              <div className="styles-intro">
                <h3>Artist Style Lookup</h3>
                <p>Search for an artist to get their musical style description. Use this for AI music generation prompts.</p>
              </div>
              
              <div className="artist-search-section">
                <div className="artist-search-wrapper">
                  <input
                    ref={artistSearchRef}
                    id="artist-search"
                    type="text"
                    className="artist-search-input"
                    placeholder="Search for an artist (e.g., Taylor Swift, Drake)..."
                    value={artistSearch}
                    onChange={(e) => {
                      setArtistSearch(e.target.value);
                      setShowArtistDropdown(true);
                      if (!e.target.value) {
                        setSelectedArtistStyle('');
                      }
                    }}
                    onFocus={() => setShowArtistDropdown(true)}
                    autoComplete="off"
                  />
                  <svg className="search-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                  
                  {showArtistDropdown && artistSearch && filteredArtists.length > 0 && (
                    <ul ref={dropdownRef} className="artist-dropdown" role="listbox">
                      {filteredArtists.slice(0, 8).map((artist) => (
                        <li
                          key={artist}
                          className="artist-dropdown-item"
                          onClick={() => handleSelectArtist(artist)}
                          role="option"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSelectArtist(artist); }}
                        >
                          <svg viewBox="0 0 20 20" fill="currentColor">
                            <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                          </svg>
                          <span dangerouslySetInnerHTML={{
                            __html: artist.replace(
                              new RegExp(`(${artistSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
                              '<strong>$1</strong>'
                            )
                          }} />
                        </li>
                      ))}
                      {filteredArtists.length > 8 && (
                        <li className="artist-dropdown-more">+{filteredArtists.length - 8} more results...</li>
                      )}
                    </ul>
                  )}

                  {showArtistDropdown && artistSearch && filteredArtists.length === 0 && (
                    <div className="artist-dropdown artist-no-results">
                      No artists found matching "{artistSearch}"
                    </div>
                  )}
                </div>

                {(selectedArtistStyle || isLoadingStyle) && (
                  <div className="artist-style-result">
                    <div className="style-header">
                      <span className="style-label">Style Prompt:</span>
                      {selectedArtistStyle && !isLoadingStyle && (
                        <button className={`copy-btn ${styleCopied ? 'copied' : ''}`} onClick={handleCopyStyle}>
                          {styleCopied ? 'Copied!' : 'Copy'}
                        </button>
                      )}
                    </div>
                    <div className="style-content">
                      {isLoadingStyle ? (
                        <div className="style-loading"><div className="spinner"></div>Loading style...</div>
                      ) : (
                        <code>{selectedArtistStyle}</code>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Admin Panel */}
          {activeTab === 'admin' && user?.isAdmin && (
            <div id="admin-panel" role="tabpanel" aria-labelledby="admin-tab" className="tab-panel">
              <div className="admin-header">
                <h2>Admin Panel</h2>
                <p>Manage users, view credits and spending</p>
              </div>
              
              {adminLoading ? (
                <div className="admin-loading">
                  <div className="spinner"></div>
                  <span>Loading users...</span>
                </div>
              ) : (
                <div className="admin-table-container">
                  <table className="admin-table" aria-label="User management table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>IP Address</th>
                        <th>Credits</th>
                        <th>Spent</th>
                        <th>Registered</th>
                        <th>Admin</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminUsers.map(u => (
                        <tr key={u.id} className={u.is_admin ? 'admin-row' : ''}>
                          <td>{u.id}</td>
                          <td>{u.name || <span className="text-muted">—</span>}</td>
                          <td>{u.email || <span className="text-muted">—</span>}</td>
                          <td className="ip-cell">{u.ip_address || <span className="text-muted">—</span>}</td>
                          <td className="credits-cell">{u.credits}</td>
                          <td className="spent-cell">${u.money_spent?.toFixed(2) || '0.00'}</td>
                          <td>{u.is_registered ? <span className="badge badge-success">Yes</span> : <span className="badge badge-muted">No</span>}</td>
                          <td>{u.is_admin ? <span className="badge badge-admin">Admin</span> : <span className="badge badge-muted">—</span>}</td>
                          <td className="actions-cell">
                            <button 
                              className="btn-icon btn-edit" 
                              onClick={() => handleEditUser(u)}
                              aria-label={`Edit user ${u.name || u.id}`}
                            >
                              <svg viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                            </button>
                            {!u.is_admin && u.id !== user.id && (
                              <button 
                                className="btn-icon btn-delete" 
                                onClick={() => handleDeleteUser(u)}
                                aria-label={`Delete user ${u.name || u.id}`}
                              >
                                <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  <div className="admin-summary">
                    <span>Total users: {adminUsers.length}</span>
                    <span>Registered: {adminUsers.filter(u => u.is_registered).length}</span>
                    <span>Total spent: ${adminUsers.reduce((sum, u) => sum + (u.money_spent || 0), 0).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <footer className="footer">
        <p>Powered by phonetic magic ✨</p>
      </footer>
    </div>
  );
}

export default App;

